import { t, Trans } from '@lingui/macro'
import {
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import {
  addAccountContactAffiliation,
  setAcountAffiliationToFormer
} from 'app/services/sfAuth/sfData/sfAccount'
import { checkContactUpdateRequest, createCaseByFlow } from 'app/services/sfAuth/sfData/sfCase'
import {
  checkContactByFlow,
  createContactByFlow,
  mapContact,
  saveContactByFlow,
  saveContactFromLookupField
} from 'app/services/sfAuth/sfData/sfContact'
import { filterUsers } from 'app/services/sfAuth/sfData/sfUser'
import ContactSelector from 'app/views/common-components/contact-selector/ContactSelector'
import { TooltipLabelIcon } from 'app/views/common-components/TooltipLabelIcon'
import { parseExtensionPhoneToSF } from 'app/views/common/Formats'
import { Field, useFormikContext } from 'formik'
import { isNil } from 'lodash'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { myI18n } from 'translation/I18nConnectedProvider'
import { isDebugUser } from 'utilsTS'
import * as Yup from 'yup'
import { getMuState } from '../../../common/Common'
import { FormConnectToObject } from '../../../common/FormConnectToObject'
import { getMainConnected } from '../../../Form'
import { requiredTrans } from '../../../formTranslations'
import { MUEditedByLabel } from '../../../multiuser/components/MUEditedByLabel'
import {
  endEditingField,
  startEditingField
} from '../../../multiuser/grpcMultiuserEdit'

import { useEffect } from 'react'

// export const formContactPickerExtractSaveKey = ({ saveObj, value, item }) => {
//   const saveAt = item.typeProps.connectedField.name
//   saveObj[saveAt] = value.id
// }

export const formContactPickerSavePromise = ({
  value,
  item,
  connectedObject,
  additionalSFInfo
}) => {
  const objectName = connectedObject.attributes.type
  const objectId = connectedObject.Id
  const { affiliationRole } = item.typeProps
  const { connectedField } = getMainConnected(item)
  const fieldName = connectedField?.name
  if (!value || !fieldName) {
    return { promise: null }
  }
  const { affiliation } = value
  const fieldValue = value.id

  return {
    promise: saveContactFromLookupField({
      objectName,
      objectId,
      fieldName,
      fieldValue
    }).then(result => {
      if (affiliationRole && !affiliation) {
        return addAccountContactAffiliation({
          role: affiliationRole,
          contactId: fieldValue,
          accountId: objectId
        })
      } else if (
        affiliationRole &&
        affiliation &&
        affiliation.npe5__Contact__c.Id !== fieldValue
      ) {
        return Promise.all([
          addAccountContactAffiliation({
            role: affiliationRole,
            contactId: fieldValue,
            accountId: objectId
          }),
          setAcountAffiliationToFormer(affiliation.Id)
        ])
      } else {
        return result
      }
    }),
    afterMainPromise: false
  }
}

export const formContactParseValueToCompare = (value, props) => {
  if (props.saveFailed && value) {
    let toRet = value.firstName + ' ' + value.lastName
    if (value.email) {
      toRet += ', ' + value.email
    }
    return toRet
  }
  if (value && value.id) {
    return value.id
  } else {
    return value
  }
}

export const formContactPickerValueToText = (
  value,
  question,
  { contactsMap }
) => {
  if (typeof value === 'string') {
    const contact = contactsMap[value] || {}
    let toRet = contact.FirstName + ' ' + contact.LastName
    if (contact.Email) {
      toRet += ', ' + contact.Email
    }
    return {
      en: toRet,
      fr: toRet
    }
  } else if (!value) {
    return {
      en: '',
      fr: ''
    }
  }
  let toRet = value.firstName + ' ' + value.lastName
  if (value.email) {
    toRet += ', ' + value.email
  }
  if (value.jobTitle) {
    toRet += ', ' + value.jobTitle
  }
  return {
    en: toRet,
    fr: toRet
  }
}

export const formContactPickerValidation = (item, data, formikRef) => {
  const schema = {}

  schema.firstName = Yup.string().nullable().required(requiredTrans)
  schema.lastName = Yup.string().nullable().required(requiredTrans)
  schema.email = Yup.string()
    .nullable()
    .required(requiredTrans)
    .email(<Trans>This must be a valid email</Trans>)

  schema.phoneNumber = Yup.string()
    .nullable()
    .required(requiredTrans)
    .test(
      'isValidPhone',
      () => <Trans>Phone Number must have at least 10 digits</Trans>,
      value => {
        if (!isNil(value)) {
          const filtered = value.replace(/[^0-9]/g, '')
          return filtered.length >= 10
        }
        return true
      }
    )

  schema.phoneNumberCountryCode = Yup.string().nullable().required(requiredTrans)
  // schema.providedOrganizationName = Yup.string().nullable().required(requiredTrans)

  schema.jobTitle = Yup.string().nullable().required(requiredTrans)

  return Yup.mixed()
    .test(
      'objectExists',
      () => <Trans>REQUIRED_CONTACT_ERROR</Trans>,
      value => value && typeof value === 'object' && Object.keys(value).length > 0
    )
    .test(
      'objectShape',
      () => <Trans>MISSING_INFORMATION_ERROR</Trans>,
      async function (value) {
        if (!value || typeof value !== 'object') {
          return true
        }
        if (value.hasActiveCaseRequest) {
          return true
        }
        try {
          await Yup.object().shape(schema).validate(value, { abortEarly: false })
          return true
        } catch (err) {
          return false
        }
      }
    )
}

export const formContactPickerDefaultValue = (obj, additionalInfo, item) => {
  const { contactsMap = {}, accountAffiliations = [], contactReviewMap = {} } = additionalInfo
  if (!obj) {
    return null
  } else {
    const { connectedField } = getMainConnected(item)
    const { affiliationRole } = item.typeProps
    const field = connectedField?.name
    if (obj[field] && contactsMap[obj[field]]) {
      const contact = contactsMap[obj[field]]
      let relatedAffiliation
      if (affiliationRole && accountAffiliations) {
        accountAffiliations.some(affiliation => {
          if (
            affiliation.npe5__Role__c === affiliationRole &&
            affiliation.npe5__Status__c === 'Current'
          ) {
            relatedAffiliation = affiliation
            return true
          } else {
            return false
          }
        })
      }
      return {
        ...mapContact(contact),
        affiliation: relatedAffiliation,
        hasActiveCaseRequest: contactReviewMap[obj[field]] === true
      }
    } else {
      return obj[field]
    }
  }
}

export const FormContactPickerPrint = ({ value, title, typeProps }) => {
  // const { printLabelPlacement } = typeProps
  // const top = !printLabelPlacement || printLabelPlacement === 'top'
  let valueToShow = value ? value.firstName + ' ' + value.lastName : ''
  if (valueToShow && value.jobTitle) {
    valueToShow += ', ' + value.jobTitle
  }
  if (valueToShow && value.email) {
    valueToShow += ', ' + value.email
  }

  return (
    <div>
      <Typography style={{ whiteSpace: 'pre-line' }}>{valueToShow}</Typography>
      {/* {!top && <Typography className='form-print-subtitle'>{title}</Typography>} */}
    </div>
  )
}

export const FormContactPicker = ({
  id,
  title,
  editMode,
  disabled,
  saveDisabled,
  useMultiuser,
  muBag,
  connectedObject,
  formId,
  showPrintProps,
  typeProps,
  value
}) => {
  const { values, setFieldValue } = useFormikContext()
  const { isEdited, userColor, userName } = getMuState({ values, id })
  const organization = useSelector(state => state.organization)
  const organizationId = organization.id
  const currentUser = useSelector(state => state.user)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const {
    connectedTo = [],
    defaultOrganization,
    workEmailInContactCreation,
    printLabelPlacement
  } = typeProps
  const top = !printLabelPlacement || printLabelPlacement === 'top'

  // hasActiveCaseRequest is embedded on the value by formContactPickerDefaultValue
  // at load time and kept up-to-date by onContactFound / updateContact below.
  const hasActiveCaseRequest = value?.hasActiveCaseRequest

  if (showPrintProps) {
    return (
      <div>
        {top && (
          <Typography className='form-print-subtitle'>{title}</Typography>
        )}
        <Typography style={{ whiteSpace: 'pre-line' }}>
          CONTACT INFO PLACEHOLDER
        </Typography>
        {!top && (
          <Typography className='form-print-subtitle'>{title}</Typography>
        )}
      </div>
    )
  }

  return (
    <Field name={id}>
      {({ field: { value }, meta }) => {
        const searchContacts = async ({ values }) => {
          const flow = await checkContactByFlow(values)
          const result = flow[0].outputValues
          const filteredUsers = filterUsers({
            currentUser,
            users: result.contactsFound,
            disallowedStrings: isDebugUser(currentUser)
              ? ['DUPLICATE']
              : ['FAKE', 'TESTER', 'DUPLICATE']
          })
          if (filteredUsers?.length === 0) {
            return { contacts: [], error: 'NOT_FOUND' }
          }
          if (result.error != null) {
            return { contacts: [], error: result.error }
          }
          const parsedContacts = filteredUsers.map((contact) => mapContact(contact))
          return { contacts: parsedContacts, error: null }
        }

        const createNewContact = async (contactValues) => {
          const creatingSnackbar = enqueueSnackbar(
            <Trans>Creating new contact</Trans>,
            { variant: 'info', persist: true }
          )
          const toCreate = {
            title: contactValues.jobTitle,
            firstName: contactValues.firstName,
            lastName: contactValues.lastName,
            phone: parseExtensionPhoneToSF(
              contactValues.phoneNumber,
              contactValues.phoneNumberCountryCode
            ),
            providedOrganizationName: contactValues.providedOrganizationName
          }
          if (workEmailInContactCreation) {
            toCreate.workEmail = contactValues.email
          } else {
            toCreate.email = contactValues.email
          }
          let hasError = false
          try {
            const result = await createContactByFlow(toCreate)
            closeSnackbar(creatingSnackbar)
            if (isNil(result.error) || result.error === '') {
              enqueueSnackbar(
                <Trans>Contact created!</Trans>,
                { variant: 'success' }
              )
              onContactFound({ ...contactValues, id: result.newContactId })
            }
            if (result.error) {
              hasError = true
              let errorTrans = <Trans>Contact with this name already exists!</Trans>
              if (result.error === 'NAME_ALREADY_IN_USE') {
                errorTrans = <Trans>Contact with this name already exists!</Trans>
              }
              if (!isNil(errorTrans)) {
                enqueueSnackbar(errorTrans, { variant: 'error' })
              }
              throw result.error
            }
            return result
          } catch (error) {
            closeSnackbar(creatingSnackbar)
            if (!hasError) {
              enqueueSnackbar(
                <Trans>Error occurred while creating new contact!</Trans>,
                { variant: 'error' }
              )
            }
            throw error
          }
        }

        const onContactFound = async (contact) => {
          let hasActiveCaseRequest = false
          if (contact.id && organizationId) {
            hasActiveCaseRequest = Boolean(
              await checkContactUpdateRequest(contact.id, organizationId)
            )
          }
          const newValue = { ...value, ...contact, hasActiveCaseRequest }
          setFieldValue(id, newValue)

          if (useMultiuser) {
            endEditingField({
              ...muBag,
              fieldId: id,
              fieldValue: newValue,
              lockId: values.muInfo.lockId
            })
          }
        }

        const onContactUpdate = contact => {
          setFieldValue(id, { ...value, ...contact })
          if (useMultiuser) {
            endEditingField({
              ...muBag,
              fieldId: id,
              fieldValue: { ...value, ...contact },
              lockId: values.muInfo.lockId
            })
          }
        }

        const updateContact = async ({ contact, isValid, initialValues }) => {
          const isContactOwner = contact.id === currentUser.id
          if (isContactOwner) {
            const snackbar = enqueueSnackbar(<Trans>UPDATING_CONTACT_SNACKBAR_TITLE</Trans>, { variant: 'info' })
            try {
              await saveContactByFlow(contact)
              closeSnackbar(snackbar)
              enqueueSnackbar(<Trans>Contact updated!</Trans>, { variant: 'info' })
              onContactUpdate(contact)
            } catch (error) {
              closeSnackbar(snackbar)
              console.error(error)
              enqueueSnackbar(
                <Trans>Error ocurred while updating contact!</Trans>,
                { variant: 'error' }
              )
              throw error
            }
          } else {
            let body = isContactOwner
              ? ''
              : myI18n._(t`NEW_CONTACT_WAS_SUBMITTED_BY`) + ' ' + currentUser.displayName + ', ' + currentUser?.userObject?.jobTitle + '\n\n'

            const textData = [
              { key: 'firstName', text: myI18n._(t`First name`) },
              { key: 'lastName', text: myI18n._(t`Last name`) },
              { key: 'email', text: myI18n._(t`Email`) },
              { key: 'jobTitle', text: myI18n._(t`Job title`) },
              {
                key: 'providedOrganizationName',
                text: myI18n._(t`Organization`)
              },
              { key: 'changeReason', text: myI18n._(t`CHANGE_REASON_INPUT_LABEL`) }

            ]
            textData.forEach(obj => {
              const { key, text } = obj
              if (contact[key] !== initialValues[key]) {
                body += text + ': ' + contact[key] + '\n'
              }
            })
            if (
              contact.phoneNumber !== initialValues.phoneNumber ||
              contact.phoneNumberCountryCode !== initialValues.phoneNumberCountryCode
            ) {
              body += myI18n._(t`Phone number`) +
              ': ' + parseExtensionPhoneToSF(contact.phoneNumber, contact.phoneNumberCountryCode)
            }
            const toCreate = {
              title: 'Update contact information',
              type: 'Request contact information update',
              contact: contact.id,
              language: currentUser.language,
              userName: currentUser.displayName,
              organization: organization.id,
              description: body
            }
            const snackbar = enqueueSnackbar(<Trans>SUBMITTING_CONTACT_TO_REVIEW_SNACKBAR_TITLE</Trans>, { variant: 'info' })
            try {
              await createCaseByFlow(toCreate)
              setFieldValue(id, { ...value, hasActiveCaseRequest: true })
              closeSnackbar(snackbar)
              enqueueSnackbar(<Trans>Information submitted!</Trans>, { variant: 'info' })
            } catch (error) {
              closeSnackbar(snackbar)
              enqueueSnackbar(<Trans>Error Submitting Case</Trans>, { variant: 'error' })
              console.error(error)
              throw error
            }
          }
        }

        const isContactFound = Boolean(value && typeof value === 'object')

        return (
          <>
            <Card
              sx={isContactFound && disabled
                ? {
                    borderRadius: 0,
                    padding: '24px 24px 24px 32px',
                    border: '2px solid #00000042'
                  }
                : isContactFound
                  ? {
                      borderRadius: 0,
                      padding: '24px 24px 24px 32px',
                      border: '1px solid black'
                    }
                  : {
                      border: 'none',
                      background: 'transparent'
                    }}
            >
              <Grid container direction='column' justify='center'>
                <div>
                  <ContactSelector
                    error={meta.error}
                    preview={saveDisabled}
                    searchContacts={searchContacts}
                    createNewContact={createNewContact}
                    updateContact={updateContact}
                    onDialogClose={e => {
                      if (useMultiuser) {
                        endEditingField({
                          ...muBag,
                          fieldId: id,
                          fieldValue: value,
                          lockId: values.muInfo.lockId
                        })
                      }
                    }}
                    onDialogOpen={e => {
                      if (useMultiuser) {
                        startEditingField({
                          ...muBag,
                          fieldId: id
                        })
                      }
                    }}
                    disabled={
                      disabled ||
                      editMode ||
                      connectedTo.length === 0 ||
                      Boolean(isEdited && useMultiuser)
                    }
                    onSelect={onContactFound}
                    currentOrganizationAsDefault={
                      defaultOrganization === 'current'
                    }
                    values={value}
                    hasActiveCaseRequest={hasActiveCaseRequest}
                  />
                </div>
              </Grid>
            </Card>
            {/* <FormErrorLabel id={id} error={meta.error} /> */}
            {isEdited && (
              <div style={{ marginTop: 5 }}>
                <MUEditedByLabel color={userColor} userName={userName} />
              </div>
            )}
          </>
        )
      }}
    </Field>
  )
}

export const FormEditorContactPicker = ({
  editMode,
  showPrintProps,
  depth,
  typeProps,
  injectable,
  injectableId,
  disabled,
  ...props
}) => {
  const {
    required,
    affiliationRole,
    defaultOrganization,
    workEmailInContactCreation
  } = typeProps
  const dispatch = useDispatch()
  const { values } = useFormikContext()
  const { connectedObject } = getMainConnected({
    typeProps,
    ...props
  })

  let connectedToAccount
  if (
    connectedObject &&
    values.objectsConnected.some(
      obj => obj.identId === connectedObject && obj.type === 'Account'
    )
  ) {
    connectedToAccount = true
  }

  useEffect(() => {
    if (!connectedToAccount && affiliationRole) {
      const toSet = { ...typeProps }
      delete toSet.affiliationRole
      dispatch({
        type: 'FIELD',
        depth: depth.split('.'),
        fieldName: 'typeProps',
        fieldValue: toSet
      })
    }
  }, [connectedToAccount])

  if (!editMode) {
    return (
      <FormContactPicker
        editMode
        typeProps={typeProps}
        showPrintProps={showPrintProps}
        {...props}
      />
    )
  }

  return (
    <div>
      {!showPrintProps && (
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(required)}
                disabled={disabled}
                onChange={e => {
                  const toSet = { ...typeProps }
                  toSet.required = e.target.checked
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
            }
            label={<Trans>Required</Trans>}
          />
        </div>
      )}
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(workEmailInContactCreation)}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.workEmailInContactCreation = e.target.checked
                dispatch({
                  type: 'FIELD',
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: { ...toSet }
                })
              }}
            />
          }
          label={<Trans>Use work email in contact creation</Trans>}
        />
      </div>
      <TextField
        variant='outlined'
        label={<Trans>Default organization</Trans>}
        fullWidth
        select
        value={defaultOrganization || ''}
        style={{ marginTop: 5 }}
        onChange={e => {
          const toSet = { ...typeProps }
          toSet.defaultOrganization = e.target.value
          dispatch({
            type: 'FIELD',
            depth: depth.split('.'),
            fieldName: 'typeProps',
            fieldValue: toSet
          })
        }}
      >
        <MenuItem value='current' key='current'>
          <Trans>Current Organization</Trans>
        </MenuItem>
        <MenuItem value='household' key='household'>
          <Trans>Household</Trans>
        </MenuItem>
      </TextField>
      {connectedToAccount && (
        <Grid container>
          <Grid item xs style={{ paddingRight: 5 }}>
            <TextField
              variant='outlined'
              label={<Trans>Account affiliation role</Trans>}
              fullWidth
              value={affiliationRole || ''}
              style={{ marginTop: 10 }}
              onChange={e => {
                const toSet = { ...typeProps }
                toSet.affiliationRole = e.target.value
                dispatch({
                  type: 'FIELD',
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: toSet
                })
              }}
            />
          </Grid>
          <TooltipLabelIcon
            tooltip={
              <Trans>
                If account affiliation role is not empty, the affiliation object
                will be created and updated when contact is picked
              </Trans>
            }
          />
        </Grid>
      )}
      <FormConnectToObject
        injectable={injectable}
        injectableId={injectableId}
        typeProps={typeProps}
        depth={depth}
        filter={item =>
          item.type === 'reference' && item.referenceTo.includes('Contact')}
      />
    </div>
  )
}
